<template>
  <div class="edit-keybox-panel">
    <div v-if="loadingKeybox" class="edit-keybox-panel__loading">
      <b-spinner variant="primary" style="width:3rem;height:3rem"/>
      <p class="mt-2 mb-0 text-muted">{{ msg('Loading keybox') }}...</p>
      <p class="small text-muted mt-50">{{ msg('Fetching device status') }}...</p>
    </div>
    <template v-else>
    <div class="edit-keybox-panel__body">
      <!-- Device info -->
      <div class="edit-keybox-panel__section">
        <div class="edit-keybox-panel__section-header">
          <feather-icon icon="KeyIcon" size="18" class="text-primary"/>
          <h6 class="mb-0">{{ msg('Device info') }}</h6>
        </div>
        <b-row>
          <b-col cols="12" md="6">
            <b-form-group :label="msg('IP Address / Hostname')" label-size="sm">
              <b-form-input v-model="keybox.ipAddress" size="sm" placeholder="192.168.0.10:PORT"/>
            </b-form-group>
          </b-col>
          <b-col cols="12" md="6">
            <b-form-group :label="msg('Serial Number')" label-size="sm">
              <b-form-input v-model="keybox.serialNumber" size="sm" placeholder="06e79d9d00720f31"/>
            </b-form-group>
          </b-col>
          <b-col cols="12" md="6">
            <b-form-group :label="msg('Device Pass')" label-size="sm">
              <b-form-input v-model="keybox.devicePass" size="sm" type="password" placeholder="111111"/>
            </b-form-group>
          </b-col>
          <b-col cols="12" md="6">
            <b-form-group :label="msg('Location')" label-size="sm">
              <b-form-input v-model="keybox.location" size="sm"/>
            </b-form-group>
          </b-col>
          <b-col cols="12" md="6">
            <b-form-group :label="msg('Status')" label-size="sm">
              <b-form-select v-model="keybox.status" size="sm" :options="statuses"/>
            </b-form-group>
          </b-col>
          <b-col cols="12" md="6">
            <b-form-group :label="msg('Installation Status')" label-size="sm">
              <b-form-select v-model="keybox.installationStatus" size="sm" :options="installationStatuses"/>
            </b-form-group>
          </b-col>
        </b-row>
      </div>

      <!-- Assignments -->
      <div class="edit-keybox-panel__section">
        <div class="edit-keybox-panel__section-header">
          <feather-icon icon="BuildingIcon" size="18" class="text-primary"/>
          <h6 class="mb-0">{{ msg('Assignments') }}</h6>
        </div>
        <b-row>
          <b-col cols="12" md="6">
            <b-form-group :label="msg('Branch')" label-size="sm">
              <v-select v-model="keybox.buid" :options="allBranches" label="name" :reduce="b=>b.id"
                        :clearable="false" class="select-size-sm"/>
            </b-form-group>
          </b-col>
          <b-col cols="12" md="6">
            <b-form-group :label="msg('Building')" label-size="sm">
              <building-select-box v-model="keybox.buildingId"/>
            </b-form-group>
          </b-col>
        </b-row>
      </div>

      <!-- Slots -->
      <div class="edit-keybox-panel__section">
        <div class="edit-keybox-panel__section-header">
          <feather-icon icon="LayersIcon" size="18" class="text-primary"/>
          <h6 class="mb-0">{{ msg('Slots') }}</h6>
        </div>

        <!-- Status bar -->
        <div class="edit-keybox-panel__status-bar">
          <div class="d-flex align-items-center" style="gap:12px">
            <b-button size="sm" variant="outline-secondary" @click="checkStatus" :disabled="statusChecking">
              <b-spinner v-if="statusChecking" small class="mr-50"/>
              <feather-icon v-else icon="RefreshCwIcon" size="14" class="mr-50"/>
              {{ msg('Refresh status') }}
            </b-button>
            <b-badge :variant="currentStatusVariant" pill class="px-2 py-1 font-weight-bold text-uppercase">
              <feather-icon :icon="currentStatusIcon" size="12" class="mr-50"/>
              {{ currentStatusLabel }}
              <span v-if="keyBoxStatus && keyBoxStatus.msg" class="small ml-1">{{ keyBoxStatus.msg }}</span>
            </b-badge>
          </div>
          <b-button size="sm" variant="warning" @click="openAllCompartments">
            <feather-icon icon="UnlockIcon" size="14" class="mr-50"/>
            {{ msg('Unlock all') }}
          </b-button>
        </div>

        <!-- Slot cards grid -->
        <div v-if="compartments.length" class="edit-keybox-panel__slots-grid">
          <div v-for="comp in compartments" :key="comp.number"
               class="slot-card"
               :class="{
                 'slot-card--open': comp.isOpen,
                 'slot-card--occupied': comp.isOccupied,
                 'slot-card--assigned': comp.realEstateId
               }">
            <div class="slot-card__header">
              <span class="slot-card__number">{{ msg('Slot') }} #{{ comp.number }}</span>
              <b-badge v-if="comp.isOpen!=null" :variant="comp.isOpen ? 'success' : 'secondary'" pill size="sm">
                {{ comp.isOpen ? msg('Open') : msg('Closed') }}
              </b-badge>
              <b-badge v-else variant="light-secondary" pill size="sm">{{ msg('Unknown') }}</b-badge>
            </div>
            <div class="slot-card__meta">
              <span class="text-muted small">{{ msg('Status') }}</span>
              <b-badge v-if="comp.isOccupied!=null" :variant="comp.isOccupied ? 'warning' : 'light-secondary'" pill size="sm">
                {{ comp.isOccupied ? msg('Occupied') : msg('Free') }}
              </b-badge>
              <b-badge v-else variant="light-secondary" pill size="sm">{{ msg('Unknown') }}</b-badge>
            </div>
            <div class="slot-card__room">
              <label class="small text-muted mb-50">{{ msg('Assigned Room') }}</label>
              <real-estate-picker-locker :buildings="[keybox.buildingId]" v-model="comp.realEstateId"/>
            </div>
            <b-button size="sm" variant="outline-warning" class="slot-card__unlock"
                      :disabled="comp.isOpen" @click="openCompartment(comp)">
              <feather-icon icon="UnlockIcon" size="12" class="mr-50"/>
              {{ msg('Unlock') }}
            </b-button>
          </div>
        </div>
      </div>
    </div>

    <div class="edit-keybox-panel__footer">
      <b-button variant="primary" @click="updateKeybox" :disabled="saving">
        <b-spinner v-if="saving" small class="mr-50"/>
        <feather-icon v-else icon="SaveIcon" size="16" class="mr-50"/>
        {{ msg('Save Changes') }}
      </b-button>
    </div>
    </template>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import BuildingSelectBox from '@/components/components/picker/buildingSelectBox.vue'
import RealEstatePickerLocker from '@/components/components/picker/realEstatesPickerLocker.vue'

export default {
  name: 'KeyboxEditPanel',
  components: { RealEstatePickerLocker, BuildingSelectBox },
  props: {
    id: { type: [String, Number], required: true }
  },
  data() {
    return {
      loadingKeybox: true,
      saving: false,
      statusChecking: false,
      keybox: { compartments: [], totalCompartments: 0 },
      keyBoxStatus: {},
      currentStatus: 'connected',
      compartments: [],
      statuses: [
        { value: 'online', text: 'Online' },
        { value: 'offline', text: 'Offline' },
        { value: 'error', text: 'Error' }
      ],
      installationStatuses: [
        { value: 'installed', text: 'Installed' },
        { value: 'pending', text: 'Pending' },
        { value: 'failed', text: 'Failed' }
      ]
    }
  },
  computed: {
    ...mapGetters('translator', ['msg']),
    ...mapGetters('data', ['getAllBranches']),
    allBranches() {
      return Object.values(this.getAllBranches || {})
    },
    currentStatusLabel() {
      const labels = { connected: 'Connected', disconnected: 'Disconnected', error: 'Error', checking: 'Checking...' }
      return this.msg(labels[this.currentStatus] || 'Unknown')
    },
    currentStatusVariant() {
      const v = { connected: 'success', disconnected: 'secondary', error: 'danger', checking: 'warning' }
      return v[this.currentStatus] || 'dark'
    },
    currentStatusIcon() {
      const icons = {
        connected: 'CheckCircleIcon',
        disconnected: 'WifiOffIcon',
        error: 'AlertCircleIcon',
        checking: 'RefreshCwIcon'
      }
      return icons[this.currentStatus] || 'HelpCircleIcon'
    }
  },
  created() {
    this.loadKeybox()
  },
  watch: {
    id() {
      this.loadKeybox()
    }
  },
  methods: {
    ...mapActions('keybox', ['getKeyboxById', 'update', 'openSlot', 'openAllSlots', 'checkStatusLocker']),
    async loadKeybox() {
      this.loadingKeybox = true
      try {
        const data = await this.getKeyboxById({ id: this.id })
        this.keybox = { ...data }
        this.compartments = [...(data.compartments || [])]
        await this.checkStatus()
      } catch (e) {
        console.error('Failed to load keybox', e)
      } finally {
        this.loadingKeybox = false
      }
    },
    openCompartment(comp) {
      this.openSlot({ keyboxId: this.keybox.id, slotNumber: comp.number }).then(() => this.loadKeybox())
    },
    openAllCompartments() {
      this.openAllSlots({ keyboxId: this.keybox.id }).then(() => this.loadKeybox())
    },
    checkStatus() {
      this.currentStatus = 'checking'
      this.statusChecking = true
      return this.checkStatusLocker({ keyboxId: this.id })
        .then(ans => {
          this.keyBoxStatus = ans
          const code = ans.code || 0
          const message = (ans.msg || '').toLowerCase()
          const hasData = Array.isArray(ans.data) && ans.data.length > 0
          if (code === 1 && hasData) this.currentStatus = 'connected'
          else if (message.includes('offline') || code === 0) this.currentStatus = 'error'
          else this.currentStatus = 'disconnected'
        })
        .catch(() => { this.currentStatus = 'error' })
        .finally(() => { this.statusChecking = false })
    },
    updateKeybox() {
      this.keybox.id = this.id
      this.keybox.compartments = this.compartments
      this.saving = true
      this.update({ keybox: this.keybox })
        .then(id => this.$emit('submitted', { status: true, server: id, id }))
        .catch(a => this.$emit('submitted', { status: false, server: a }))
        .finally(() => { this.saving = false })
    }
  }
}
</script>

<style scoped>
.edit-keybox-panel {
  display: flex;
  flex-direction: column;
  min-height: 200px;
}
.edit-keybox-panel__loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 280px;
  padding: 48px 24px;
}
.edit-keybox-panel__body { padding: 24px; flex: 1; }
.edit-keybox-panel__footer {
  padding: 16px 24px;
  border-top: 1px solid rgba(0,0,0,.06);
  background: rgba(0,0,0,.02);
}
.dark-layout .edit-keybox-panel__footer {
  border-top-color: rgba(255,255,255,.06);
  background: rgba(0,0,0,.2);
}
.edit-keybox-panel__section {
  margin-bottom: 24px;
  padding-bottom: 24px;
  border-bottom: 1px solid rgba(0,0,0,.06);
}
.edit-keybox-panel__section:last-of-type { border-bottom: none; margin-bottom: 0; padding-bottom: 0; }
.dark-layout .edit-keybox-panel__section { border-bottom-color: rgba(255,255,255,.06); }
.edit-keybox-panel__section-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
}
.edit-keybox-panel__section-header h6 { font-weight: 600; }

.edit-keybox-panel__status-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 16px;
  padding: 12px 16px;
  border-radius: 8px;
  background: rgba(115,103,240,.05);
  border: 1px solid rgba(115,103,240,.12);
}
.dark-layout .edit-keybox-panel__status-bar {
  background: rgba(115,103,240,.08);
  border-color: rgba(115,103,240,.2);
}

.edit-keybox-panel__slots-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 14px;
}

.slot-card {
  padding: 16px;
  border-radius: 10px;
  background: #fff;
  border: 1px solid rgba(0,0,0,.06);
  transition: all .2s ease;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.slot-card:hover {
  box-shadow: 0 4px 12px rgba(0,0,0,.08);
  border-color: rgba(115,103,240,.2);
}
.slot-card--assigned { border-left: 3px solid var(--primary); }
.slot-card--open { background: rgba(40,199,111,.06); }
.slot-card--occupied { background: rgba(255,159,67,.04); }

.slot-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 6px;
}
.slot-card__number { font-weight: 600; color: var(--primary); }
.slot-card__meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: .8rem;
}
.slot-card__room { margin-top: 4px; }
.slot-card__unlock { margin-top: auto; align-self: flex-start; }

/* Dark layout */
.dark-layout .slot-card {
  background: #283046;
  border-color: #3b4253;
}
.dark-layout .slot-card:hover {
  background: #303850;
  border-color: #444c5e;
  box-shadow: 0 4px 14px rgba(0,0,0,.4);
}
.dark-layout .slot-card--open { background: rgba(40,199,111,.12); }
.dark-layout .slot-card--occupied { background: rgba(255,159,67,.1); }
</style>
